import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/api/auth';
import { api } from '@/lib/api';
import type { RewardRuleInfo, PunishRuleInfo } from '@/lib/api/types';
import { HABIT_LABELS, HABIT_COLORS } from '@/lib/api/rewardsTypes';

const habitFilters = [
  { id: 'all', label: '全部' },
  { id: 'timeManagement', label: '时间管理', icon: 'ri-time-line' },
  { id: 'studyFocus', label: '学习专注', icon: 'ri-compasses-2-line' },
  { id: 'lifeCare', label: '生活自理', icon: 'ri-house-line' },
  { id: 'sportsHealth', label: '运动健康', icon: 'ri-heart-pulse-line' },
  { id: 'responsibility', label: '责任担当', icon: 'ri-award-line' },
  { id: 'etiquette', label: '言行礼仪', icon: 'ri-smile-line' },
];

interface RuleForm {
  name: string;
  type: 'reward' | 'punishment';
  points: string;
  habits: string[];
  description: string;
  repeatPenalty: boolean;
  penaltyMultiplier: number;
  enabled: boolean;
  dailyCheckIn: boolean;
  timerEnabled: boolean;
  deadlineHour: number;
  deadlineMinute: number;
  earlyBonus: number;
  earlyBonusInterval: number;
}

interface RulesTabProps {
  rewardRules: RewardRuleInfo[];
  punishRules: PunishRuleInfo[];
  onRefresh: () => void;
}

// Helper functions - defined before use
const getHabitId = (type: number): string => {
  const map: Record<number, string> = {
    1: 'timeManagement',
    2: 'studyFocus',
    3: 'lifeCare',
    4: 'sportsHealth',
    5: 'responsibility',
    6: 'etiquette',
  };
  return map[type] || 'all';
};

const getHabitType = (id: string): number => {
  const map: Record<string, number> = {
    timeManagement: 1,
    studyFocus: 2,
    lifeCare: 3,
    sportsHealth: 4,
    responsibility: 5,
    etiquette: 6,
  };
  return map[id] || 0;
};

export default function RulesTab({ rewardRules, punishRules, onRefresh }: RulesTabProps) {
  const navigate = useNavigate();
  const { currentFamily } = useAuth();
  const [activeHabit, setActiveHabit] = useState('all');
  const [activeType, setActiveType] = useState<'all' | 'reward' | 'punishment'>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVipModal, setShowVipModal] = useState(false);
  const [editingRule, setEditingRule] = useState<{ id: string; type: 'reward' | 'punishment' } | null>(null);
  const [loading, setLoading] = useState(false);

  const isVip = currentFamily?.vip_status === 2;
  const canManage = currentFamily?.my_role === 1;
  const totalRules = rewardRules.length + punishRules.length;
  const canAddRule = canManage && (isVip || totalRules < 20);

  // Merge and format rules for display
  const allRules = [
    ...rewardRules.map(r => ({ ...r, type: 'reward' as const, pointsDisplay: r.points })),
    ...punishRules.map(r => ({ ...r, type: 'punishment' as const, pointsDisplay: -r.points })),
  ] as any[];

  const [form, setForm] = useState<RuleForm>({
    name: '',
    type: 'reward',
    points: '',
    habits: [],
    description: '',
    repeatPenalty: false,
    penaltyMultiplier: 1.0,
    enabled: true,
    dailyCheckIn: false,
    timerEnabled: false,
    deadlineHour: 21,
    deadlineMinute: 0,
    earlyBonus: 1,
    earlyBonusInterval: 30,
  });

  const filtered = allRules.filter(r => {
    // habit_type is now number[] (multi-habit support)
    const habitTypes: number[] = Array.isArray(r.habit_type) ? r.habit_type : (r.habit_type ? [r.habit_type] : []);
    const ruleHabitIds = habitTypes.map(h => getHabitId(h));
    const habitMatch = activeHabit === 'all' || ruleHabitIds.includes(activeHabit);
    const typeMatch = activeType === 'all' || r.type === activeType;
    return habitMatch && typeMatch;
  });

  const toggleHabit = (habit: string) => {
    setForm(prev => ({
      ...prev,
      habits: prev.habits.includes(habit) ? prev.habits.filter(h => h !== habit) : [...prev.habits, habit]
    }));
  };

  const adjustMultiplier = (delta: number) => {
    setForm(prev => ({
      ...prev,
      penaltyMultiplier: Math.max(1.0, Math.round((prev.penaltyMultiplier + delta) * 10) / 10)
    }));
  };

  const resetForm = () => {
    setForm({
      name: '',
      type: 'reward',
      points: '',
      habits: [],
      description: '',
      repeatPenalty: false,
      penaltyMultiplier: 1.0,
      enabled: true,
      dailyCheckIn: false,
      timerEnabled: false,
      deadlineHour: 21,
      deadlineMinute: 0,
      earlyBonus: 1,
      earlyBonusInterval: 30,
    });
  };

  const toggleDailyCheckIn = async (ruleId: string, type: 'reward' | 'punishment', currentValue: boolean) => {
    if (!currentFamily) return;
    setLoading(true);
    try {
      if (type === 'reward') {
        await api.rules.updateRewardRule(currentFamily.id, ruleId, {
          is_daily_checkin: !currentValue
        });
      } else {
        await api.rules.updatePunishRule(currentFamily.id, ruleId, {
          is_daily_checkin: !currentValue
        });
      }
      onRefresh();
    } catch (error: any) {
      console.error('Failed to update rule:', error);
      alert('更新失败: ' + (error?.response?.data?.detail || error?.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  const toggleEnabled = async (ruleId: string, type: 'reward' | 'punishment', currentStatus: number) => {
    if (!currentFamily) return;
    setLoading(true);
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      if (type === 'reward') {
        await api.rules.updateRewardRule(currentFamily.id, ruleId, { status: newStatus });
      } else {
        await api.rules.updatePunishRule(currentFamily.id, ruleId, { status: newStatus });
      }
      onRefresh();
    } catch (error: any) {
      console.error('Failed to update rule:', error);
      alert('更新失败: ' + (error?.response?.data?.detail || error?.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRule = async (ruleId: string, type: 'reward' | 'punishment') => {
    if (!currentFamily) return;
    if (!confirm('确定要删除该规则吗？此操作不可撤销！')) return;

    setLoading(true);
    try {
      if (type === 'reward') {
        await api.rules.deleteRewardRule(currentFamily.id, ruleId);
      } else {
        await api.rules.deletePunishRule(currentFamily.id, ruleId);
      }
      onRefresh();
    } catch (error: any) {
      console.error('Failed to delete rule:', error);
      alert('删除失败: ' + (error?.response?.data?.detail || error?.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditRule = (rule: any) => {
    // habit_type 现在是数组 [1,2,3]
    const habitTypesList = Array.isArray(rule.habit_type) ? rule.habit_type : [];
    // 将 habit_type 数字转换为 habit ID 字符串
    const habitIds = habitTypesList.map((ht: number) => getHabitId(ht));
    setForm({
      name: rule.name,
      type: rule.type,
      points: String(rule.points),
      habits: habitIds,
      description: rule.description,
      repeatPenalty: rule.consecutive_multiplier || false,
      penaltyMultiplier: rule.consecutive_ratio || 1.0,
      enabled: rule.status === 1,
      dailyCheckIn: rule.is_daily_checkin || false,
      timerEnabled: !!rule.timer_enabled,
      deadlineHour: rule.deadline_time ? parseInt(rule.deadline_time.split(':')[0]) : 21,
      deadlineMinute: rule.deadline_time ? parseInt(rule.deadline_time.split(':')[1]) : 0,
      earlyBonus: rule.early_bonus || rule.overtime_bonus || 1,
      earlyBonusInterval: rule.early_bonus_per_min || rule.overtime_bonus_per_min || 30,
    });
    setEditingRule({ id: rule.id, type: rule.type });
    setShowEditModal(true);
  };

  const openAddModal = () => {
    if (!canAddRule) {
      setShowVipModal(true);
      return;
    }
    resetForm();
    // 根据当前筛选标签设置默认规则类型
    const defaultType = activeType === 'punishment' ? 'punishment' : 'reward';
    setForm(prev => ({ ...prev, type: defaultType }));
    setShowAddModal(true);
  };

  const handleUpdateRule = async () => {
    if (!currentFamily || !editingRule) return;
    if (!form.name.trim()) {
      alert('请输入规则名称');
      return;
    }
    if (!form.points || parseFloat(form.points) === 0) {
      alert('请输入有效的积分值');
      return;
    }

    setLoading(true);
    try {
      // 将习惯 ID 转换为 habit_type 数字数组 [1,2,3]
      const habitTypesList = form.habits.map(h => getHabitType(h));

      const baseData = {
        name: form.name,
        description: form.description,
        points: Math.abs(parseFloat(form.points)),
        habit_type: habitTypesList.length > 0 ? habitTypesList : [1],
        status: form.enabled ? 1 : 0,
      };

      if (form.type === 'reward') {
        const rewardData = {
          ...baseData,
          is_daily_checkin: form.dailyCheckIn,
          timer_enabled: form.dailyCheckIn && form.timerEnabled,
          deadline_time: form.dailyCheckIn && form.timerEnabled ? `${String(form.deadlineHour).padStart(2, '0')}:${String(form.deadlineMinute).padStart(2, '0')}` : undefined,
          early_bonus: form.dailyCheckIn && form.timerEnabled ? form.earlyBonus : 0,
          early_bonus_per_min: form.dailyCheckIn && form.timerEnabled ? form.earlyBonusInterval : 0,
        };
        await api.rules.updateRewardRule(currentFamily.id, editingRule.id, rewardData as any);
      } else {
        const punishData = {
          ...baseData,
          is_daily_checkin: false,
          timer_enabled: form.timerEnabled,
          deadline_time: form.timerEnabled ? `${String(form.deadlineHour).padStart(2, '0')}:${String(form.deadlineMinute).padStart(2, '0')}` : undefined,
          overtime_bonus: form.timerEnabled ? form.earlyBonus : 0,
          overtime_bonus_per_min: form.timerEnabled ? form.earlyBonusInterval : 0,
          consecutive_multiplier: form.repeatPenalty,
          consecutive_ratio: form.penaltyMultiplier,
        };
        await api.rules.updatePunishRule(currentFamily.id, editingRule.id, punishData as any);
      }

      setShowEditModal(false);
      setEditingRule(null);
      resetForm();
      onRefresh();
    } catch (error: any) {
      console.error('Failed to update rule:', error);
      const errorMsg = error?.response?.data?.detail || error?.message || '更新失败，请稍后重试';
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRule = async () => {
    if (!currentFamily) return;
    if (!form.name.trim()) {
      alert('请输入规则名称');
      return;
    }
    if (!form.points || parseFloat(form.points) === 0) {
      alert('请输入有效的积分值');
      return;
    }

    setLoading(true);
    try {
      // 确保 habit_type 是有效的数字数组
      const habitTypes = form.habits.map(h => {
        const num = getHabitType(h);
        return Number.isInteger(num) ? num : 1; // 验证是有效数字
      }).filter(num => num >= 1 && num <= 6);
      const habitTypeToSend = habitTypes.length > 0 ? habitTypes : [1];

      // 基础数据 - habit_type 现在是验证后的数组
      const baseData = {
        name: form.name,
        description: form.description,
        points: Math.abs(parseFloat(form.points)),
        habit_type: habitTypeToSend,
        status: form.enabled ? 1 : 0,
      };

      // 奖励规则：日常打卡和计时功能关联
      if (form.type === 'reward') {
        const rewardData = {
          ...baseData,
          is_daily_checkin: form.dailyCheckIn,
          timer_enabled: form.dailyCheckIn && form.timerEnabled,
          deadline_time: form.dailyCheckIn && form.timerEnabled ? `${String(form.deadlineHour).padStart(2, '0')}:${String(form.deadlineMinute).padStart(2, '0')}` : undefined,
          early_bonus: form.dailyCheckIn && form.timerEnabled ? form.earlyBonus : 0,
          early_bonus_per_min: form.dailyCheckIn && form.timerEnabled ? form.earlyBonusInterval : 0,
        };
        await api.rules.createRewardRule(currentFamily.id, rewardData as any);
      } else {
        // 惩罚规则：计时功能独立（不依赖日常打卡）
        const punishData = {
          ...baseData,
          is_daily_checkin: false, // 惩罚规则不支持日常打卡
          timer_enabled: form.timerEnabled,
          deadline_time: form.timerEnabled ? `${String(form.deadlineHour).padStart(2, '0')}:${String(form.deadlineMinute).padStart(2, '0')}` : undefined,
          overtime_bonus: form.timerEnabled ? form.earlyBonus : 0,
          overtime_bonus_per_min: form.timerEnabled ? form.earlyBonusInterval : 0,
          consecutive_multiplier: form.repeatPenalty,
          consecutive_ratio: form.penaltyMultiplier,
        };
        await api.rules.createPunishRule(currentFamily.id, punishData as any);
      }

      setShowAddModal(false);
      resetForm();
      onRefresh();
    } catch (error: any) {
      console.error('Failed to save rule:', error);
      const errorMsg = error?.response?.data?.detail || error?.message || '保存失败，请稍后重试';
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex gap-1 bg-blue-50 p-1 rounded-xl border border-blue-100">
          {(['all', 'reward', 'punishment'] as const).map(t => (
            <button key={t} onClick={() => setActiveType(t)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeType === t ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t === 'all' ? '全部' : t === 'reward' ? '🏆 奖励规则' : '⚠️ 惩罚规则'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={() => navigate('/market')}
            className="flex items-center gap-2 text-amber-600 bg-amber-50 border border-amber-200 px-4 py-2 rounded-xl text-sm font-medium hover:bg-amber-100 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-store-2-line"></i>激励市场
          </button>
          <button onClick={openAddModal}
            className={`flex items-center gap-2 text-white px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              canAddRule
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 hover:opacity-90'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            <i className="ri-add-line"></i>添加规则
            {!canAddRule && <span className="text-[10px]">({totalRules}/20)</span>}
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        {habitFilters.map(h => (
          <button key={h.id} onClick={() => setActiveHabit(h.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
              activeHabit === h.id ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-500 border-gray-200 hover:border-blue-400 hover:text-blue-500'
            }`}
          >
            {h.icon && <i className={`${h.icon} text-xs mr-1`}></i>}
            {h.id === 'all' ? '全部' : h.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map(rule => (
          <div key={rule.id} className={`bg-white rounded-2xl p-4 border-l-4 transition-all hover:-translate-y-0.5 border border-blue-100 ${rule.type === 'reward' ? 'border-l-emerald-400' : 'border-l-rose-400'} ${rule.status !== 1 ? 'opacity-60' : ''}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${rule.type === 'reward' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500'}`}>
                    {rule.type === 'reward' ? '奖励' : '惩罚'}
                  </span>
                  {rule.is_daily_checkin && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 flex items-center gap-0.5 border border-amber-100">
                      <i className="ri-checkbox-circle-fill text-xs"></i>日常打卡
                    </span>
                  )}
                  {'consecutive_multiplier' in rule && rule.consecutive_multiplier && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-50 text-orange-500 flex items-center gap-0.5">
                      <i className="ri-stack-fill text-xs"></i>×{rule.consecutive_ratio}倍
                    </span>
                  )}
                  {rule.status !== 1 && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-400">已停用</span>}
                </div>
                <h4 className="font-bold text-blue-900 text-sm truncate">{rule.name}</h4>
              </div>
              <div className="text-right ml-3 flex-shrink-0">
                <span className={`font-black text-lg ${rule.type === 'reward' ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {rule.type === 'reward' ? '+' : ''}{rule.type === 'reward' ? rule.points : -rule.points}
                </span>
                <div className="text-gray-400 text-[10px]">积分</div>
              </div>
            </div>
            <p className="text-gray-500 text-xs leading-relaxed mb-2 line-clamp-2">{rule.description}</p>
            <div className="flex flex-wrap gap-1 mb-3">
              {(() => {
                const habitMap: Record<number, string> = {
                  1: '时间管理',
                  2: '学习专注',
                  3: '生活自理',
                  4: '运动健康',
                  5: '责任担当',
                  6: '言行礼仪'
                };
                // habit_type 现在是数组 [1,2,3]
                const habitTypes = Array.isArray(rule.habit_type) ? rule.habit_type : [];
                return habitTypes.map((ht: number) => (
                  <span key={ht} className="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                    {habitMap[ht] || '其他'}
                  </span>
                ));
              })()}
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-blue-50">
              <div className="flex gap-3">
                <div className="text-center">
                  <div className="text-sm font-bold text-blue-900">{rule.trigger_count}</div>
                  <div className="text-[10px] text-gray-400">触发次</div>
                </div>
                <div className="text-center">
                  <div className={`text-sm font-bold ${rule.points >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                    {rule.points >= 0 ? '+' : ''}{rule.points * rule.trigger_count}
                  </div>
                  <div className="text-[10px] text-gray-400">累计分</div>
                </div>
              </div>
              <div className="flex gap-1.5 items-center">
                {/* 打卡按钮只显示给奖励规则 */}
                {rule.type === 'reward' && (
                  <button onClick={() => toggleDailyCheckIn(rule.id, rule.type, rule.is_daily_checkin)}
                    disabled={loading}
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium border transition-all cursor-pointer whitespace-nowrap ${
                      rule.is_daily_checkin ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-gray-50 text-gray-400 border-gray-200 hover:border-amber-300 hover:text-amber-500'
                    }`}
                  >
                    <i className={`${rule.is_daily_checkin ? 'ri-calendar-check-fill' : 'ri-calendar-check-line'} text-xs`}></i>
                    {rule.is_daily_checkin ? '打卡中' : '加打卡'}
                  </button>
                )}
                <button onClick={() => {
                  if (!canManage) { alert('记录员无权修改规则'); return; }
                  toggleEnabled(rule.id, rule.type, rule.status);
                }}
                  disabled={loading || !canManage}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg border transition-colors ${
                    canManage ? 'border-gray-200 text-gray-400 hover:border-blue-400 hover:text-blue-400 cursor-pointer' : 'border-gray-100 text-gray-300 cursor-not-allowed'
                  }`}>
                  <i className={`ri-${rule.status === 1 ? 'pause' : 'play'}-circle-line text-sm`}></i>
                </button>
                <button
                  onClick={() => {
                    if (!canManage) { alert('记录员无权修改规则'); return; }
                    const ruleData = allRules.find(r => r.id === rule.id);
                    if (ruleData) handleEditRule(ruleData);
                  }}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg border transition-colors ${
                    canManage ? 'border-gray-200 text-gray-400 hover:border-blue-500 hover:text-blue-500 cursor-pointer' : 'border-gray-100 text-gray-300 cursor-not-allowed'
                  }`}>
                  <i className="ri-edit-line text-sm"></i>
                </button>
                <button onClick={() => {
                  if (!canManage) { alert('记录员无权删除规则'); return; }
                  handleDeleteRule(rule.id, rule.type);
                }}
                  disabled={loading || !canManage}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg border transition-colors ${
                    canManage ? 'border-gray-200 text-gray-400 hover:border-red-400 hover:text-red-400 cursor-pointer' : 'border-gray-100 text-gray-300 cursor-not-allowed'
                  }`}>
                  <i className="ri-delete-bin-6-line text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Rule Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[520px] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white px-6 pt-6 pb-4 border-b border-gray-50 flex items-center justify-between z-10">
              <h3 className="font-bold text-blue-900 flex items-center gap-2">
                <i className={`ri-${form.type === 'reward' ? 'trophy' : 'alert'}-fill text-${form.type === 'reward' ? 'emerald' : 'rose'}-500`}></i>
                {form.type === 'reward' ? '添加奖励规则' : '添加惩罚规则'}
              </h3>
              <button onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Type */}
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">规则类型</label>
                <div className="flex gap-2">
                  {(['reward', 'punishment'] as const).map(t => (
                    <button key={t} onClick={() => setForm({ ...form, type: t })}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                        form.type === t ? t === 'reward' ? 'border-emerald-400 text-emerald-600 bg-emerald-50' : 'border-rose-400 text-rose-500 bg-rose-50' : 'border-gray-200 text-gray-500'
                      }`}
                    >
                      <i className={t === 'reward' ? 'ri-trophy-fill' : 'ri-alert-fill'}></i>
                      {t === 'reward' ? '奖励规则' : '惩罚规则'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Enable */}
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2">
                  <i className="ri-toggle-fill text-blue-500 text-base"></i>
                  <span className="text-sm font-semibold text-gray-700">启用规则</span>
                </div>
                <button onClick={() => setForm({ ...form, enabled: !form.enabled })}
                  className={`w-12 h-6 rounded-full transition-all cursor-pointer relative ${form.enabled ? 'bg-blue-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.enabled ? 'left-6' : 'left-0.5'}`}></div>
                </button>
              </div>

              {/* Daily check-in - Only for reward rules */}
              {form.type === 'reward' && (
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl border border-amber-100">
                  <div className="flex items-center gap-2">
                    <i className="ri-calendar-check-fill text-amber-500 text-base"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">日常打卡任务</p>
                      <p className="text-xs text-amber-600">启用后加入每日打卡清单，进行习惯跟踪</p>
                    </div>
                  </div>
                  <button onClick={() => setForm({ ...form, dailyCheckIn: !form.dailyCheckIn, timerEnabled: false })}
                    className={`w-12 h-6 rounded-full transition-all cursor-pointer relative flex-shrink-0 ${form.dailyCheckIn ? 'bg-amber-500' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.dailyCheckIn ? 'left-6' : 'left-0.5'}`}></div>
                  </button>
                </div>
              )}

              {/* Timer */}
              {((form.type === 'reward' && form.dailyCheckIn) || form.type === 'punishment') && (
              <div className="bg-cyan-50 rounded-xl border border-cyan-100 overflow-hidden">
                <div className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-2">
                    <i className="ri-timer-fill text-cyan-500 text-base"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">计时功能</p>
                      <p className="text-xs text-cyan-600">{form.type === 'reward' ? '设置截止时间，提前完成可获额外奖励积分' : '设置截止时间，超时将额外扣除积分'}</p>
                    </div>
                  </div>
                  <button onClick={() => setForm({ ...form, timerEnabled: !form.timerEnabled })}
                    className={`w-12 h-6 rounded-full transition-all cursor-pointer relative flex-shrink-0 ${form.timerEnabled ? 'bg-cyan-500' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.timerEnabled ? 'left-6' : 'left-0.5'}`}></div>
                  </button>
                </div>
                {form.timerEnabled && (
                  <div className="px-4 pb-4 space-y-3 border-t border-cyan-100 pt-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-2 block">截止时间</label>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 bg-white rounded-xl border border-cyan-200 px-3 py-2">
                          <button onClick={() => setForm({ ...form, deadlineHour: Math.max(0, form.deadlineHour - 1) })}
                            className="w-6 h-6 flex items-center justify-center text-cyan-500 hover:bg-cyan-50 rounded cursor-pointer">
                            <i className="ri-subtract-line text-xs"></i>
                          </button>
                          <span className="text-base font-black text-cyan-700 w-8 text-center">{String(form.deadlineHour).padStart(2, '0')}</span>
                          <button onClick={() => setForm({ ...form, deadlineHour: Math.min(23, form.deadlineHour + 1) })}
                            className="w-6 h-6 flex items-center justify-center text-cyan-500 hover:bg-cyan-50 rounded cursor-pointer">
                            <i className="ri-add-line text-xs"></i>
                          </button>
                        </div>
                        <span className="text-lg font-black text-gray-400">:</span>
                        <div className="flex items-center gap-1 bg-white rounded-xl border border-cyan-200 px-3 py-2">
                          <button onClick={() => setForm({ ...form, deadlineMinute: form.deadlineMinute === 0 ? 30 : 0 })}
                            className="w-6 h-6 flex items-center justify-center text-cyan-500 hover:bg-cyan-50 rounded cursor-pointer">
                            <i className="ri-subtract-line text-xs"></i>
                          </button>
                          <span className="text-base font-black text-cyan-700 w-8 text-center">{String(form.deadlineMinute).padStart(2, '0')}</span>
                          <button onClick={() => setForm({ ...form, deadlineMinute: form.deadlineMinute === 0 ? 30 : 0 })}
                            className="w-6 h-6 flex items-center justify-center text-cyan-500 hover:bg-cyan-50 rounded cursor-pointer">
                            <i className="ri-add-line text-xs"></i>
                          </button>
                        </div>
                        <span className="text-sm text-gray-500">截止</span>
                      </div>
                      <p className="text-xs text-cyan-600 mt-1.5">
                        <i className="ri-information-fill mr-0.5"></i>
                        截止时间{form.type === 'reward' ? '前' : '后'}完成，你{form.type === 'reward' ? '能得' : '要扣'} <strong>{form.points || 'N'}</strong> 基础分 + 额外分
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-2 block">{form.type === 'reward' ? '提前奖励' : '超时惩罚'}设置</label>
                      <div className="bg-white rounded-xl border border-cyan-200 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">每{form.type === 'reward' ? '提前' : '超时'}</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setForm({ ...form, earlyBonusInterval: Math.max(10, form.earlyBonusInterval - 10) })}
                              className="w-6 h-6 flex items-center justify-center rounded-lg border border-cyan-200 text-cyan-500 cursor-pointer hover:bg-cyan-50">
                              <i className="ri-subtract-line text-xs"></i>
                            </button>
                            <span className="text-sm font-bold text-cyan-700 w-8 text-center">{form.earlyBonusInterval}</span>
                            <button onClick={() => setForm({ ...form, earlyBonusInterval: Math.min(120, form.earlyBonusInterval + 10) })}
                              className="w-6 h-6 flex items-center justify-center rounded-lg border border-cyan-200 text-cyan-500 cursor-pointer hover:bg-cyan-50">
                              <i className="ri-add-line text-xs"></i>
                            </button>
                            <span className="text-xs text-gray-500 ml-1">分钟</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">{form.type === 'reward' ? '额外奖励' : '额外惩罚'}</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setForm({ ...form, earlyBonus: Math.max(1, form.earlyBonus - 1) })}
                              className="w-6 h-6 flex items-center justify-center rounded-lg border border-cyan-200 text-cyan-500 cursor-pointer hover:bg-cyan-50">
                              <i className="ri-subtract-line text-xs"></i>
                            </button>
                            <span className={`text-sm font-bold w-8 text-center ${form.type === 'reward' ? 'text-emerald-600' : 'text-rose-500'}`}>{form.earlyBonus}</span>
                            <button onClick={() => setForm({ ...form, earlyBonus: form.earlyBonus + 1 })}
                              className="w-6 h-6 flex items-center justify-center rounded-lg border border-cyan-200 text-cyan-500 cursor-pointer hover:bg-cyan-50">
                              <i className="ri-add-line text-xs"></i>
                            </button>
                            <span className="text-xs text-gray-500 ml-1">分</span>
                          </div>
                        </div>
                        <div className="bg-cyan-50 rounded-lg p-2 text-xs text-cyan-700">
                          <i className="ri-lightbulb-fill mr-1 text-amber-500"></i>
                          {form.type === 'reward'
                            ? `例如：${String(form.deadlineHour).padStart(2, '0')}:${String(form.deadlineMinute).padStart(2, '0')} 前完成得基础分，每提前 ${form.earlyBonusInterval} 分钟额外奖励 ${form.earlyBonus} 分`
                            : `例如：${String(form.deadlineHour).padStart(2, '0')}:${String(form.deadlineMinute).padStart(2, '0')} 前完成免罚，但超时就要扣基础分，每超时 ${form.earlyBonusInterval} 分钟额外惩罚 ${form.earlyBonus} 分`}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">规则名称</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="例如：完成当日作业"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">积分数量</label>
                <input type="number" value={form.points} onChange={e => setForm({ ...form, points: e.target.value })} placeholder="请输入积分数量（正数）"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">关联习惯（可多选）</label>
                <div className="flex flex-wrap gap-2">
                  {habitFilters.filter(h => h.id !== 'all').map(h => (
                    <button key={h.id} onClick={() => toggleHabit(h.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer border whitespace-nowrap ${
                        form.habits.includes(h.id) ? HABIT_COLORS[h.id] + ' text-white border-' + HABIT_COLORS[h.id].replace('bg-', '') + '500' : 'bg-white text-gray-500 border-gray-200'
                      }`}
                    >{h.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">规则描述</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="详细描述触发条件..." rows={2} maxLength={500}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 resize-none" />
              </div>

              {form.type === 'punishment' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl border border-orange-100">
                    <button onClick={() => setForm({ ...form, repeatPenalty: !form.repeatPenalty })}
                      className={`w-12 h-6 rounded-full transition-all cursor-pointer relative flex-shrink-0 ${form.repeatPenalty ? 'bg-orange-500' : 'bg-gray-300'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.repeatPenalty ? 'left-6' : 'left-0.5'}`}></div>
                    </button>
                    <div>
                      <p className="text-sm font-medium text-orange-700">开启连续惩罚倍率</p>
                      <p className="text-xs text-orange-500">重复犯错时按倍数递增扣除</p>
                    </div>
                  </div>
                  {form.repeatPenalty && (
                    <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-orange-700">连续惩罚倍率</span>
                        <span className="text-base font-black text-orange-600">×{form.penaltyMultiplier}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        <button onClick={() => adjustMultiplier(-0.1)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-orange-200 text-orange-500 hover:bg-orange-100 cursor-pointer">
                          <i className="ri-subtract-line text-sm"></i>
                        </button>
                        <div className="flex-1 text-center font-bold text-orange-600 text-lg">{form.penaltyMultiplier}</div>
                        <button onClick={() => adjustMultiplier(0.1)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-orange-200 text-orange-500 hover:bg-orange-100 cursor-pointer">
                          <i className="ri-add-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button onClick={handleSaveRule}
                disabled={loading}
                className="w-full text-white py-3 rounded-xl font-bold hover:opacity-90 transition-all cursor-pointer whitespace-nowrap bg-gradient-to-r from-blue-500 to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                确认添加规则
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Rule Modal */}
      {showEditModal && editingRule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[520px] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white px-6 pt-6 pb-4 border-b border-gray-50 flex items-center justify-between z-10">
              <h3 className="font-bold text-blue-900 flex items-center gap-2">
                <i className={`ri-${form.type === 'reward' ? 'trophy' : 'alert'}-fill text-${form.type === 'reward' ? 'emerald' : 'rose'}-500`}></i>
                {form.type === 'reward' ? '编辑奖励规则' : '编辑惩罚规则'}
              </h3>
              <button onClick={() => setShowEditModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Type */}
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">规则类型</label>
                <div className="flex gap-2">
                  {(['reward', 'punishment'] as const).map(t => (
                    <button key={t} onClick={() => setForm({ ...form, type: t })}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                        form.type === t ? t === 'reward' ? 'border-emerald-400 text-emerald-600 bg-emerald-50' : 'border-rose-400 text-rose-500 bg-rose-50' : 'border-gray-200 text-gray-500'
                      }`}
                    >
                      <i className={t === 'reward' ? 'ri-trophy-fill' : 'ri-alert-fill'}></i>
                      {t === 'reward' ? '奖励规则' : '惩罚规则'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Enable */}
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2">
                  <i className="ri-toggle-fill text-blue-500 text-base"></i>
                  <span className="text-sm font-semibold text-gray-700">启用规则</span>
                </div>
                <button onClick={() => setForm({ ...form, enabled: !form.enabled })}
                  className={`w-12 h-6 rounded-full transition-all cursor-pointer relative ${form.enabled ? 'bg-blue-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.enabled ? 'left-6' : 'left-0.5'}`}></div>
                </button>
              </div>

              {/* Daily check-in - Only for reward rules */}
              {form.type === 'reward' && (
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl border border-amber-100">
                  <div className="flex items-center gap-2">
                    <i className="ri-calendar-check-fill text-amber-500 text-base"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">日常打卡任务</p>
                      <p className="text-xs text-amber-600">启用后加入每日打卡清单，进行习惯跟踪</p>
                    </div>
                  </div>
                  <button onClick={() => setForm({ ...form, dailyCheckIn: !form.dailyCheckIn, timerEnabled: false })}
                    className={`w-12 h-6 rounded-full transition-all cursor-pointer relative flex-shrink-0 ${form.dailyCheckIn ? 'bg-amber-500' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.dailyCheckIn ? 'left-6' : 'left-0.5'}`}></div>
                  </button>
                </div>
              )}

              {/* Timer */}
              {form.dailyCheckIn && (
              <div className="bg-cyan-50 rounded-xl border border-cyan-100 overflow-hidden">
                <div className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-2">
                    <i className="ri-timer-fill text-cyan-500 text-base"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">计时功能</p>
                      <p className="text-xs text-cyan-600">{form.type === 'reward' ? '设置截止时间，提前完成可获额外奖励积分' : '设置截止时间，超时将额外扣除积分'}</p>
                    </div>
                  </div>
                  <button onClick={() => setForm({ ...form, timerEnabled: !form.timerEnabled })}
                    className={`w-12 h-6 rounded-full transition-all cursor-pointer relative flex-shrink-0 ${form.timerEnabled ? 'bg-cyan-500' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.timerEnabled ? 'left-6' : 'left-0.5'}`}></div>
                  </button>
                </div>
                {form.timerEnabled && (
                  <div className="px-4 pb-4 space-y-3 border-t border-cyan-100 pt-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-2 block">截止时间</label>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 bg-white rounded-xl border border-cyan-200 px-3 py-2">
                          <button onClick={() => setForm({ ...form, deadlineHour: Math.max(0, form.deadlineHour - 1) })}
                            className="w-6 h-6 flex items-center justify-center text-cyan-500 hover:bg-cyan-50 rounded cursor-pointer">
                            <i className="ri-subtract-line text-xs"></i>
                          </button>
                          <span className="text-base font-black text-cyan-700 w-8 text-center">{String(form.deadlineHour).padStart(2, '0')}</span>
                          <button onClick={() => setForm({ ...form, deadlineHour: Math.min(23, form.deadlineHour + 1) })}
                            className="w-6 h-6 flex items-center justify-center text-cyan-500 hover:bg-cyan-50 rounded cursor-pointer">
                            <i className="ri-add-line text-xs"></i>
                          </button>
                        </div>
                        <span className="text-lg font-black text-gray-400">:</span>
                        <div className="flex items-center gap-1 bg-white rounded-xl border border-cyan-200 px-3 py-2">
                          <button onClick={() => setForm({ ...form, deadlineMinute: form.deadlineMinute === 0 ? 30 : 0 })}
                            className="w-6 h-6 flex items-center justify-center text-cyan-500 hover:bg-cyan-50 rounded cursor-pointer">
                            <i className="ri-subtract-line text-xs"></i>
                          </button>
                          <span className="text-base font-black text-cyan-700 w-8 text-center">{String(form.deadlineMinute).padStart(2, '0')}</span>
                          <button onClick={() => setForm({ ...form, deadlineMinute: form.deadlineMinute === 0 ? 30 : 0 })}
                            className="w-6 h-6 flex items-center justify-center text-cyan-500 hover:bg-cyan-50 rounded cursor-pointer">
                            <i className="ri-add-line text-xs"></i>
                          </button>
                        </div>
                        <span className="text-sm text-gray-500">截止</span>
                      </div>
                      <p className="text-xs text-cyan-600 mt-1.5">
                        <i className="ri-information-fill mr-0.5"></i>
                        截止时间前完成，你能得 <strong>{form.points || 'N'}</strong> 分
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-2 block">提前奖励设置</label>
                      <div className="bg-white rounded-xl border border-cyan-200 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">每提前</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setForm({ ...form, earlyBonusInterval: Math.max(10, form.earlyBonusInterval - 10) })}
                              className="w-6 h-6 flex items-center justify-center rounded-lg border border-cyan-200 text-cyan-500 cursor-pointer hover:bg-cyan-50">
                              <i className="ri-subtract-line text-xs"></i>
                            </button>
                            <span className="text-sm font-bold text-cyan-700 w-8 text-center">{form.earlyBonusInterval}</span>
                            <button onClick={() => setForm({ ...form, earlyBonusInterval: Math.min(120, form.earlyBonusInterval + 10) })}
                              className="w-6 h-6 flex items-center justify-center rounded-lg border border-cyan-200 text-cyan-500 cursor-pointer hover:bg-cyan-50">
                              <i className="ri-add-line text-xs"></i>
                            </button>
                            <span className="text-xs text-gray-500 ml-1">分钟</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">{form.type === 'reward' ? '额外奖励' : '额外惩罚'}</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setForm({ ...form, earlyBonus: Math.max(1, form.earlyBonus - 1) })}
                              className="w-6 h-6 flex items-center justify-center rounded-lg border border-cyan-200 text-cyan-500 cursor-pointer hover:bg-cyan-50">
                              <i className="ri-subtract-line text-xs"></i>
                            </button>
                            <span className={`text-sm font-bold w-8 text-center ${form.type === 'reward' ? 'text-emerald-600' : 'text-rose-500'}`}>{form.earlyBonus}</span>
                            <button onClick={() => setForm({ ...form, earlyBonus: form.earlyBonus + 1 })}
                              className="w-6 h-6 flex items-center justify-center rounded-lg border border-cyan-200 text-cyan-500 cursor-pointer hover:bg-cyan-50">
                              <i className="ri-add-line text-xs"></i>
                            </button>
                            <span className="text-xs text-gray-500 ml-1">分</span>
                          </div>
                        </div>
                        <div className="bg-cyan-50 rounded-lg p-2 text-xs text-cyan-700">
                          <i className="ri-lightbulb-fill mr-1 text-amber-500"></i>
                          {form.type === 'reward'
                            ? `例如：${String(form.deadlineHour).padStart(2, '0')}:${String(form.deadlineMinute).padStart(2, '0')} 前完成得基础分，每提前 ${form.earlyBonusInterval} 分钟额外奖励 ${form.earlyBonus} 分`
                            : `例如：${String(form.deadlineHour).padStart(2, '0')}:${String(form.deadlineMinute).padStart(2, '0')} 前完成免罚，超时每 ${form.earlyBonusInterval} 分钟额外惩罚 ${form.earlyBonus} 分`}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">规则名称</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="例如：完成当日作业"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">积分数量</label>
                <input type="number" value={form.points} onChange={e => setForm({ ...form, points: e.target.value })} placeholder="请输入积分数量（正数）"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">关联习惯（可多选）</label>
                <div className="flex flex-wrap gap-2">
                  {habitFilters.filter(h => h.id !== 'all').map(h => (
                    <button key={h.id} onClick={() => toggleHabit(h.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer border whitespace-nowrap ${
                        form.habits.includes(h.id) ? HABIT_COLORS[h.id] + ' text-white border-' + HABIT_COLORS[h.id].replace('bg-', '') + '500' : 'bg-white text-gray-500 border-gray-200'
                      }`}>{h.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">规则描述</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="详细描述触发条件..." rows={2} maxLength={500}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 resize-none" />
              </div>

              {form.type === 'punishment' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl border border-orange-100">
                    <button onClick={() => setForm({ ...form, repeatPenalty: !form.repeatPenalty })}
                      className={`w-12 h-6 rounded-full transition-all cursor-pointer relative flex-shrink-0 ${form.repeatPenalty ? 'bg-orange-500' : 'bg-gray-300'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.repeatPenalty ? 'left-6' : 'left-0.5'}`}></div>
                    </button>
                    <div>
                      <p className="text-sm font-medium text-orange-700">开启连续惩罚倍率</p>
                      <p className="text-xs text-orange-500">重复犯错时按倍数递增扣除</p>
                    </div>
                  </div>
                  {form.repeatPenalty && (
                    <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-orange-700">连续惩罚倍率</span>
                        <span className="text-base font-black text-orange-600">×{form.penaltyMultiplier}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        <button onClick={() => adjustMultiplier(-0.1)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-orange-200 text-orange-500 hover:bg-orange-100 cursor-pointer">
                          <i className="ri-subtract-line text-sm"></i>
                        </button>
                        <div className="flex-1 text-center font-bold text-orange-600 text-lg">{form.penaltyMultiplier}</div>
                        <button onClick={() => adjustMultiplier(0.1)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-orange-200 text-orange-500 hover:bg-orange-100 cursor-pointer">
                          <i className="ri-add-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button onClick={handleUpdateRule}
                disabled={loading}
                className="w-full text-white py-3 rounded-xl font-bold hover:opacity-90 transition-all cursor-pointer whitespace-nowrap bg-gradient-to-r from-blue-500 to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                确认保存修改
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIP限制弹窗 */}
      {showVipModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowVipModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[360px] p-6 text-center" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 mx-auto mb-4">
              <i className="ri-vip-crown-fill text-white text-3xl"></i>
            </div>
            <h3 className="font-black text-blue-900 text-lg mb-2">免费版限制</h3>
            <p className="text-gray-500 text-sm mb-4">免费版最多创建 <strong>20条</strong> 奖惩规则（当前{totalRules}条），升级VIP可无限创建！</p>
            <button onClick={() => { setShowVipModal(false); navigate('/vip'); }} className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-white py-3 rounded-xl font-bold cursor-pointer whitespace-nowrap mb-2">
              立即升级 VIP
            </button>
            <button onClick={() => setShowVipModal(false)} className="text-gray-400 text-sm cursor-pointer">暂不升级</button>
          </div>
        </div>
      )}
    </div>
  );
}
